import { signerToEcdsaValidator } from "@zerodev/ecdsa-validator";
import { createKernelAccount, createKernelAccountClient, createZeroDevPaymasterClient, getUserOperationGasPrice } from "@zerodev/sdk";
import { KERNEL_V3_1, getEntryPoint } from "@zerodev/sdk/constants";
import { createPublicClient, http, type Address, type Chain } from "viem";
import { base, baseSepolia } from 'viem/chains';
import { defaultLogger } from '../utils/logger';
import { magic } from './magic';

// ZeroDev configuration
const ZERODEV_PROJECT_ID = process.env.REACT_APP_ZERODEV_PROJECT_ID;
const ZERODEV_BUNDLER_RPC = process.env.REACT_APP_ZERODEV_BUNDLER_RPC;
const ZERODEV_PAYMASTER_RPC = process.env.REACT_APP_ZERODEV_PAYMASTER_RPC;

if (!ZERODEV_PROJECT_ID) {
  throw new Error('REACT_APP_ZERODEV_PROJECT_ID is not defined');
}

if (!ZERODEV_BUNDLER_RPC) {
  throw new Error('REACT_APP_ZERODEV_BUNDLER_RPC is not defined');
}

// Get network configuration from environment
const getNetworkUrl = () => process.env.REACT_APP_RPC_URL || 'https://sepolia.base.org';
const getChainId = () => Number(process.env.REACT_APP_PUBLIC_CHAIN_ID_DEC || 84532);

// Use Base Mainnet (8453) or Base Sepolia (84532) based on env
const chain = (getChainId() === 8453 ? base : baseSepolia) as Chain;

// Create public client for Base Sepolia
const publicClient = createPublicClient({
  transport: http(getNetworkUrl()),
  chain,
});

export interface SmartAccountConfig {
  address: Address;
  kernelClient: any;
  publicClient: typeof publicClient;
}

/**
 * Create a ZeroDev Kernel account client using Magic as the signer
 */
export async function createSmartAccount(): Promise<SmartAccountConfig> {
  try {
    const magicProvider = magic.rpcProvider;
    
    if (!magicProvider) {
      throw new Error('Failed to get Magic provider');
    }

    const ecdsaValidator = await signerToEcdsaValidator(publicClient, {
      signer: magicProvider as any,
      entryPoint: getEntryPoint("0.7"),
      kernelVersion: KERNEL_V3_1
    });

    const kernelAccount = await createKernelAccount(publicClient, {
      plugins: {
        sudo: ecdsaValidator,
      },
      entryPoint: getEntryPoint("0.7"),
      kernelVersion: KERNEL_V3_1,
    });

    // Create paymaster client if gas sponsorship is available
    let paymasterConfig: any = undefined;
    if (ZERODEV_PAYMASTER_RPC) {
      const paymasterClient = createZeroDevPaymasterClient({
        chain,
        transport: http(ZERODEV_PAYMASTER_RPC),
      });
      
      paymasterConfig = {
        getPaymasterData: async (userOperation: any, retryCount = 0) => {
          const maxRetries = 2; // Retry up to 2 times (3 total attempts)
          
          try {
            const result = await paymasterClient.sponsorUserOperation({ userOperation });
            return result;
          } catch (error: any) {
            defaultLogger.error('❌ Paymaster sponsorship failed', { 
              message: error?.shortMessage || error?.message,
              code: error?.code,
              retryCount,
              maxRetries
            });
            
            // Auto-retry for gas sponsorship failures
            if (retryCount < maxRetries) {
              defaultLogger.info(`🔄 Retrying gas sponsorship (attempt ${retryCount + 1}/${maxRetries + 1})`);
              // Wait a bit before retrying (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
              return (paymasterConfig as any).getPaymasterData(userOperation, retryCount + 1);
            }
            
            // After max retries, throw the error
            const sponsorshipError = new Error('Gas sponsorship failed after multiple attempts. Please try again.');
            sponsorshipError.name = 'GasSponsorshipError';
            sponsorshipError.cause = error;
            throw sponsorshipError;
          }
        }
      };
    }

    // Create Kernel account client with bundler gas estimation
    const kernelClient = createKernelAccountClient({
      account: kernelAccount,
      chain,
      client: publicClient,
      bundlerTransport: http(ZERODEV_BUNDLER_RPC),
      paymaster: paymasterConfig,
      userOperation: {
        estimateFeesPerGas: async ({ bundlerClient }) => {
          return await getUserOperationGasPrice(bundlerClient);
        }
      }
    });

    return {
      address: kernelAccount.address,
      kernelClient,
      publicClient,
    };
  } catch (error) {
    defaultLogger.error('❌ Failed to create smart account:', error);
    throw error;
  }
}

/**
 * Get the current smart account address
 * NOTE: This must create the Kernel account to get the correct address,
 * not just the validator address!
 */
export async function getSmartAccountAddress(): Promise<Address | null> {
  try {
    const magicProvider = magic.rpcProvider;
    if (!magicProvider) return null;

    // Create ECDSA validator
    const ecdsaValidator = await signerToEcdsaValidator(publicClient, {
      signer: magicProvider as any,
      entryPoint: getEntryPoint("0.7"),
      kernelVersion: KERNEL_V3_1,
    });

    // Create Kernel account to get the actual smart account address
    const kernelAccount = await createKernelAccount(publicClient, {
      plugins: {
        sudo: ecdsaValidator,
      },
      entryPoint: getEntryPoint("0.7"),
      kernelVersion: KERNEL_V3_1,
    });

    defaultLogger.info('📋 getSmartAccountAddress result', { address: kernelAccount.address });
    return kernelAccount.address;
  } catch (error) {
    defaultLogger.error('❌ Failed to get smart account address', error);
    return null;
  }
}

/**
 * Check if gas sponsorship is available
 */
export function isGasSponsorshipAvailable(): boolean {
  return !!(ZERODEV_PROJECT_ID && ZERODEV_BUNDLER_RPC && ZERODEV_PAYMASTER_RPC);
}

/**
 * Get ZeroDev configuration for gas sponsorship
 */
export function getZeroDevConfig() {
  if (!isGasSponsorshipAvailable()) {
    throw new Error('ZeroDev configuration is incomplete. Please set all required environment variables.');
  }

  return {
    projectId: ZERODEV_PROJECT_ID,
    bundlerRpc: ZERODEV_BUNDLER_RPC,
    paymasterRpc: ZERODEV_PAYMASTER_RPC,
    entryPoint: getEntryPoint("0.7"),
    kernelVersion: KERNEL_V3_1,
  };
}
